import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { PinField } from '@/components/PinField/PinField';
import { TextField } from '@/components/TextField/TextField';

import styles from './SignInForm.module.css';

export type SignInStage = 'email' | 'pin';

export interface SignInFormProps {
  stage: SignInStage;
  /** The address the pin was sent to; shown on the pin step. */
  email?: string;
  pending?: boolean;
  error?: string | null;
  /** On the pin step, whether the pin finishes a sign-in or a new account. */
  purpose?: 'signin' | 'signup';
  onRequestPin: (email: string) => void;
  onSubmitPin: (pin: string) => void;
  onStartOver: () => void;
  onCreateAccount?: () => void;
}

/**
 * Presentational two-step sign-in. It holds the text being typed — that is UI
 * state, not application state — and knows nothing about Turnstile: what
 * happens to an address or a pin is entirely the container's business.
 */
export function SignInForm({
  stage,
  email = '',
  pending = false,
  error = null,
  purpose = 'signin',
  onRequestPin,
  onSubmitPin,
  onStartOver,
  onCreateAccount,
}: SignInFormProps) {
  const [address, setAddress] = useState(email);
  const [pin, setPin] = useState('');
  const [refusals, setRefusals] = useState(0);
  const [lastError, setLastError] = useState(error);

  // Each new refusal replays the shake, so a second wrong pin still visibly lands.
  if (error !== lastError) {
    setLastError(error);
    if (error) {
      setRefusals(refusals + 1);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (stage === 'email') {
      onRequestPin(address.trim());
    } else {
      onSubmitPin(pin.trim());
    }
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.intro}>
        <span className={`${styles.eyebrow} ${stage === 'pin' ? styles.eyebrowPin : ''}`}>
          {stage === 'email' ? 'Developer portal' : 'Check your inbox'}
        </span>
        <h1 className={styles.heading}>{stage === 'email' ? 'Sign in' : 'Enter your pin'}</h1>
        <p className={styles.lede}>
          {stage === 'email' ? (
            'No password to remember. We will email you a one-time pin.'
          ) : (
            <>
              Enter the pin we sent to <span className={styles.recipient}>{email}</span>.
            </>
          )}
        </p>
      </div>

      <div className={styles.fields} key={refusals} data-shake={refusals > 0 || undefined}>
        {error ? <Callout tone="danger">{error}</Callout> : null}

        {stage === 'email' ? (
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@company.com"
            autoFocus
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            required
          />
        ) : (
          <PinField
            label="One-time pin"
            name="pin"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            invalid={Boolean(error)}
            value={pin}
            onChange={setPin}
            required
          />
        )}
      </div>

      <div className={styles.actions}>
        <Button type="submit" size="lg" block loading={pending}>
          {stage === 'email' ? 'Send pin' : purpose === 'signup' ? 'Create account' : 'Sign in'}
        </Button>
        {stage === 'email' && onCreateAccount ? (
          <p className={styles.switch}>
            New to LendWell?{' '}
            <button type="button" className={styles.link} onClick={onCreateAccount}>
              Create an account
            </button>
          </p>
        ) : null}
        {stage === 'pin' ? (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            block
            icon="←"
            onClick={onStartOver}
            disabled={pending}
          >
            Use another email
          </Button>
        ) : null}
      </div>
    </form>
  );
}
